import { z } from "zod";

import {
  RoleDeletionRequestStatusSchema,
  RoleManagementActionSchema,
  RoleResourceAccessRequestStatus,
  RoleResourceAccessRequestStatusSchema
} from "../../../../shared/domain/roles/role-membership.types";
import { RoleSchema } from "./core";

/**
 * Role-management governance schemas — who may create or delete a Role, whether
 * that needs approval, and the request objects filed when it does.
 */

/** A member's request to create a Role, filed instead of creating one directly (NEX-3199). */
export const RoleCreationRequestSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  requestedByUserId: z.string(),
  /** The caller's chosen owner for the Role once approved — null on legacy rows filed before this column existed. */
  ownerUserId: z.string().nullable(),
  name: z.string(),
  jobDescription: z.string().nullable(),
  status: RoleResourceAccessRequestStatusSchema,
  reviewedByUserId: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  createdRoleId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});

export type RoleCreationRequest = z.infer<typeof RoleCreationRequestSchema>;

/**
 * A member's request to delete an EXISTING Role, filed instead of deleting directly (NEX-3199).
 *
 * `status` can be `SUPERSEDED` — the Role was deleted through another door while
 * the request was open — and then `reviewedByUserId` and `reviewedAt` stay null,
 * because nobody reviewed it. `roleId` keeps naming the Role after it is gone.
 */
export const RoleDeletionRequestSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  organizationId: z.string(),
  requestedByUserId: z.string(),
  status: RoleDeletionRequestStatusSchema,
  reviewedByUserId: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
});

export type RoleDeletionRequest = z.infer<typeof RoleDeletionRequestSchema>;

/**
 * One action's governance settings — the shape the settings panel renders one
 * row from, and one row per action that HAS an approval queue.
 *
 * 🚨 IT CARRIED A `grants` ARRAY AND NO LONGER DOES. That array reported
 * `RoleManagementGrant`, the org-wide allow-list of the retired
 * `RoleManagementAction` authorization axis — a permission that granted nothing,
 * rendered as though it granted something. `RoleCapability` is the only
 * authorization vocabulary on the Roles surface now, and its org-scoped grants
 * have no editor yet; when they get one it is a new contract, not this field
 * coming back.
 */
export const RoleManagementActionSettingsSchema = z.object({
  action: RoleManagementActionSchema,
  requiresApproval: z.boolean()
});

export type RoleManagementActionSettings = z.infer<typeof RoleManagementActionSettingsSchema>;

export const RoleManagementSettingsResponseSchema = z.object({
  settings: z.array(RoleManagementActionSettingsSchema)
});

export const RoleManagementActionPathVarsSchema = z.object({
  action: RoleManagementActionSchema
});

export const UpdateRoleManagementPolicyBodySchema = z.object({
  requiresApproval: z.boolean()
});

export const RoleManagementRequestsQuerySchema = z.object({
  status: RoleResourceAccessRequestStatusSchema.optional()
});

/** The deletion queue's filter — its own, because only a deletion request can be `SUPERSEDED`. */
export const RoleDeletionRequestsQuerySchema = z.object({
  status: RoleDeletionRequestStatusSchema.optional()
});

export const RoleCreationRequestsListResponseSchema = z.object({
  requests: z.array(RoleCreationRequestSchema)
});

export const RoleDeletionRequestsListResponseSchema = z.object({
  requests: z.array(RoleDeletionRequestSchema)
});

/** APPROVED/REJECTED only — a request is never set back to PENDING through this endpoint. */
export const ReviewRoleManagementRequestBodySchema = z.object({
  status: z.enum([
    RoleResourceAccessRequestStatus.APPROVED,
    RoleResourceAccessRequestStatus.REJECTED
  ])
});

export const RoleManagementRequestIdPathVarsSchema = z.object({
  id: z.string().uuid()
});

/**
 * CreateOrRequest's outcome (NEX-3199): either the Role was created directly
 * (an org:admin, who is never held for review, or any authorized caller in an
 * organisation whose CREATE_ROLE policy does not require approval), or a
 * RoleCreationRequest was filed instead and the Role does not exist yet.
 */
export const RoleCreateResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("created"), role: RoleSchema }),
  z.object({ status: z.literal("pending"), request: RoleCreationRequestSchema })
]);

/** DeleteOrRequest's outcome (NEX-3199) — same shape as RoleCreateResultSchema, for delete. */
export const RoleDeleteResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("deleted") }),
  z.object({ status: z.literal("pending"), request: RoleDeletionRequestSchema })
]);
